/**
 * React Query hooks for the Cap Table screen.
 *
 * Mutations invalidate the shared upstream queries (summary, config,
 * shareholders, transactions, documents, announcements) so the
 * Overview KPIs stay in sync with the per-tab tables after edits.
 *
 * Cap-table data is sensitive and changes slowly — 60s polling is
 * sufficient for everything except announcements (30s, in case of
 * a fresh "Series A closed" broadcast).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateAnnouncementInput,
  CreateDocumentInput,
  CreateShareholderInput,
  UpdateAnnouncementInput,
  UpdateShareConfigInput,
  UpdateShareholderInput,
  createAnnouncement,
  createDocument,
  createShareholder,
  deleteAnnouncement,
  deleteDocument,
  deleteShareholder,
  fetchAnnouncements,
  fetchCapTableSummary,
  fetchDocuments,
  fetchShareConfig,
  fetchShareTransactions,
  fetchShareholders,
  inviteShareholder,
  updateAnnouncement,
  updateShareConfig,
  updateShareholder
} from '@/api/capTable';

const POLL = {
  fast: 30_000,
  medium: 60_000,
  slow: 5 * 60_000
};

const invalidateCore = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ['capTableSummary'] });
  qc.invalidateQueries({ queryKey: ['shareholders'] });
  qc.invalidateQueries({ queryKey: ['shareTransactions'] });
};

export function useCapTableSummary() {
  return useQuery({
    queryKey: ['capTableSummary'],
    queryFn: fetchCapTableSummary,
    refetchInterval: POLL.medium
  });
}

export function useShareConfig() {
  return useQuery({
    queryKey: ['shareConfig'],
    queryFn: fetchShareConfig,
    refetchInterval: POLL.slow
  });
}

export function useUpdateShareConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateShareConfigInput) => updateShareConfig(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shareConfig'] });
      qc.invalidateQueries({ queryKey: ['capTableSummary'] });
      qc.invalidateQueries({ queryKey: ['shareholders'] });
    }
  });
}

export function useShareholders(params: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: ['shareholders', params],
    queryFn: () => fetchShareholders(params),
    refetchInterval: POLL.medium,
    placeholderData: prev => prev
  });
}

export function useCreateShareholder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateShareholderInput) => createShareholder(input),
    onSuccess: () => invalidateCore(qc)
  });
}

export function useUpdateShareholder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateShareholderInput) => updateShareholder(input),
    onSuccess: () => invalidateCore(qc)
  });
}

export function useDeleteShareholder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteShareholder(id),
    onSuccess: () => invalidateCore(qc)
  });
}

export function useInviteShareholder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => inviteShareholder(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shareholders'] });
    }
  });
}

export function useShareTransactions(params: {
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['shareTransactions', params],
    queryFn: () => fetchShareTransactions(params),
    refetchInterval: POLL.medium,
    placeholderData: prev => prev
  });
}

export function useShareholderDocuments(params: {
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['shareholderDocuments', params],
    queryFn: () => fetchDocuments(params),
    refetchInterval: POLL.slow,
    placeholderData: prev => prev
  });
}

export function useCreateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDocumentInput) => createDocument(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shareholderDocuments'] });
    }
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteDocument(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shareholderDocuments'] });
    }
  });
}

export function useAdminAnnouncements(params: {
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['adminAnnouncements', params],
    queryFn: () => fetchAnnouncements(params),
    refetchInterval: POLL.fast,
    placeholderData: prev => prev
  });
}

export function useCreateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAnnouncementInput) => createAnnouncement(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminAnnouncements'] });
    }
  });
}

export function useUpdateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAnnouncementInput) => updateAnnouncement(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminAnnouncements'] });
    }
  });
}

export function useDeleteAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteAnnouncement(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminAnnouncements'] });
    }
  });
}
